import api from "@/services/api";

export interface InstagramKpis {
  likes: number;
  comments: number;
  shares: number;
  reposts: number;
  saves: number;
  replies: number;
  views: number;
  reach: number;
  /** Métrica oficial total_interactions da Meta. */
  totalInteractions?: number;
}

export interface InstagramIndicators {
  interactions: number;
  engagementRate: number;
  viewsPerReach: number;
  likesPer1kReach: number;
  saveRate: number;
}

export interface InstagramFollowersSnapshot {
  current: number;
  atPeriodStart: number | null;
  delta: number | null;
  growthPct: number | null;
}

export interface InstagramFollowersDayPoint {
  date: string;
  followers: number;
}

export interface InstagramNewFollowersDayPoint {
  date: string;
  newFollowers: number;
}

export interface InstagramTopPost {
  id: string;
  caption: string;
  mediaType: string;
  thumbnailUrl: string;
  permalink: string;
  timestamp: string;
  likes: number;
  comments: number;
  reach: number;
  views: number;
}

export interface InstagramDashboardData {
  username: string;
  profilePictureUrl?: string;
  kpis: InstagramKpis;
  indicators: InstagramIndicators;
  followers: InstagramFollowersSnapshot;
  periodCapped?: boolean;
}

export interface InstagramDayPoint {
  date: string;
  likes: number;
  comments: number;
  shares: number;
  reposts: number;
  saves: number;
  replies: number;
  views: number;
  reach: number;
}

export interface InstagramPublishedPerformance {
  postsCount: number;
  feedCount: number;
  reelsCount: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  reposts: number;
  views: number;
  reach: number;
  interactions: number;
  indicators: InstagramIndicators;
}

export type ProductionGalleryKind = "feed" | "reels" | "stories";

export interface MarketingContentGoals {
  feed: number;
  reels: number;
  stories: number;
}

export interface MarketingProductionView {
  feed: number;
  reels: number;
  stories: number;
  storiesFromApi: number;
  storiesManual: number | null;
}

export interface InstagramProductionItem {
  id: string;
  caption: string;
  mediaType: string;
  thumbnailUrl: string;
  permalink: string;
  timestamp: string;
  likes: number;
  comments: number;
}

export interface InstagramProductionItems {
  feed: InstagramProductionItem[];
  reels: InstagramProductionItem[];
  stories: InstagramProductionItem[];
}

export type GoalGranularity = "monthly" | "weekly" | "daily";

export interface MarketingGoalTemplates {
  monthly: Record<string, MarketingContentGoals>;
  weekly: Record<string, MarketingContentGoals>;
  daily: Record<string, MarketingContentGoals>;
}

export interface MarketingPeriod {
  startDate: string;
  endDate: string;
}

export interface InstagramAccountInfo {
  key: string;
  username: string;
  label?: string;
  profilePictureUrl?: string;
}

export interface MarketingDashboard {
  period: MarketingPeriod;
  selectedAccount: string;
  accounts: InstagramAccountInfo[];
  instagram: InstagramDashboardData | null;
  production: MarketingProductionView | null;
  productionItems: InstagramProductionItems | null;
  publishedPerformance: InstagramPublishedPerformance | null;
  goals: MarketingContentGoals;
  metricsByDay: InstagramDayPoint[];
  followersByDay: InstagramFollowersDayPoint[];
  newFollowersByDay: InstagramNewFollowersDayPoint[];
  topPosts: InstagramTopPost[];
  warnings: string[];
  configured: {
    instagram: boolean;
  };
}

export interface MarketingDashboardResponse {
  success: boolean;
  error?: string;
  period?: MarketingPeriod;
  selectedAccount?: string;
  accounts?: InstagramAccountInfo[];
  instagram?: InstagramDashboardData | null;
  production?: MarketingProductionView | null;
  productionItems?: InstagramProductionItems | null;
  publishedPerformance?: InstagramPublishedPerformance | null;
  goals?: MarketingContentGoals;
  metricsByDay?: InstagramDayPoint[];
  followersByDay?: InstagramFollowersDayPoint[];
  newFollowersByDay?: InstagramNewFollowersDayPoint[];
  topPosts?: InstagramTopPost[];
  warnings?: string[];
  configured?: MarketingDashboard["configured"];
  /** @deprecated legado — ignorado */
  kpis?: unknown;
  series?: unknown;
}

export function defaultMarketingPeriod(): MarketingPeriod {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { startDate: fmt(start), endDate: fmt(end) };
}

function normalizeDashboardResponse(data: MarketingDashboardResponse): MarketingDashboard {
  if (!data.period) {
    throw new Error(data.error ?? "Resposta inválida do servidor.");
  }

  const legacyKpis = data.kpis as { instagram?: InstagramDashboardData | null } | undefined;
  const legacySeries = data.series as { instagramMetricsByDay?: InstagramDayPoint[] } | undefined;

  return {
    period: data.period,
    selectedAccount: data.selectedAccount ?? "all",
    accounts: data.accounts ?? [],
    instagram: data.instagram ?? legacyKpis?.instagram ?? null,
    production: data.production ?? null,
    productionItems: data.productionItems ?? null,
    publishedPerformance: data.publishedPerformance ?? null,
    goals: data.goals ?? { feed: 0, reels: 0, stories: 0 },
    metricsByDay: data.metricsByDay ?? legacySeries?.instagramMetricsByDay ?? [],
    followersByDay: data.followersByDay ?? [],
    newFollowersByDay: data.newFollowersByDay ?? [],
    topPosts: data.topPosts ?? [],
    warnings: data.warnings ?? [],
    configured: data.configured ?? { instagram: false },
  };
}

export async function fetchMarketingDashboard(
  period: MarketingPeriod,
  account = "all",
  opts?: { refresh?: boolean },
): Promise<MarketingDashboard> {
  const res = await api.get<MarketingDashboardResponse>("/api/marketing/dashboard", {
    params: {
      startDate: period.startDate,
      endDate: period.endDate,
      account,
      ...(opts?.refresh ? { refresh: "1" } : {}),
    },
  });
  const data = res.data;
  if (!data.success) {
    throw new Error(data.error ?? "Não foi possível carregar o dashboard de marketing.");
  }
  return normalizeDashboardResponse(data);
}

export interface SaveMarketingGoalsInput {
  period: MarketingPeriod;
  granularity: GoalGranularity;
  reference: string;
  goals: MarketingContentGoals;
  storiesManual: number | null;
}

export async function fetchMarketingGoalsConfig(): Promise<{
  templates: MarketingGoalTemplates;
  storiesManualByPeriod: Record<string, number>;
}> {
  const res = await api.get<{
    success: boolean;
    templates: MarketingGoalTemplates;
    storiesManualByPeriod: Record<string, number>;
    error?: string;
  }>("/api/marketing/goals/config");
  if (!res.data.success) {
    throw new Error(res.data.error ?? "Não foi possível carregar metas.");
  }
  return {
    templates: res.data.templates ?? { monthly: {}, weekly: {}, daily: {} },
    storiesManualByPeriod: res.data.storiesManualByPeriod ?? {},
  };
}

export async function fetchTemplateGoals(
  granularity: GoalGranularity,
  reference: string,
): Promise<MarketingContentGoals> {
  const res = await api.get<{
    success: boolean;
    goals: MarketingContentGoals;
    error?: string;
  }>("/api/marketing/goals/config", { params: { granularity, reference } });
  if (!res.data.success) {
    throw new Error(res.data.error ?? "Não foi possível carregar metas.");
  }
  return res.data.goals ?? { feed: 0, reels: 0, stories: 0 };
}

export async function saveMarketingGoals(input: SaveMarketingGoalsInput): Promise<void> {
  const templateRes = await api.put<{ success: boolean; error?: string }>("/api/marketing/goals", {
    granularity: input.granularity,
    reference: input.reference,
    goals: input.goals,
  });
  if (!templateRes.data.success) {
    throw new Error(templateRes.data.error ?? "Não foi possível salvar as metas.");
  }

  const storiesRes = await api.put<{ success: boolean; error?: string }>("/api/marketing/goals", {
    startDate: input.period.startDate,
    endDate: input.period.endDate,
    storiesManual: input.storiesManual,
  });
  if (!storiesRes.data.success) {
    throw new Error(storiesRes.data.error ?? "Não foi possível salvar stories manual.");
  }
}

export interface MarketingReportRecipient {
  id: string;
  displayName: string;
  username: string;
  role: string;
  phoneDisplay: string;
  hasPhone: boolean;
  selected: boolean;
}

export interface MarketingReportConfig {
  enabled: boolean;
  hour: number;
  minute: number;
  recipientUserIds: string[];
  lastSentDate: string | null;
}

export interface MarketingReportConfigResponse {
  config: MarketingReportConfig;
  recipients: MarketingReportRecipient[];
  whatsappReady: boolean;
  whatsappReason: string | null;
}

export async function fetchMarketingReportConfig(): Promise<MarketingReportConfigResponse> {
  const res = await api.get<{
    success: boolean;
    error?: string;
    config: MarketingReportConfig;
    recipients: MarketingReportRecipient[];
    whatsappReady: boolean;
    whatsappReason: string | null;
  }>("/api/marketing/report-config");
  if (!res.data.success) {
    throw new Error(res.data.error ?? "Não foi possível carregar o disparo do relatório.");
  }
  return {
    config: res.data.config,
    recipients: res.data.recipients ?? [],
    whatsappReady: res.data.whatsappReady === true,
    whatsappReason: res.data.whatsappReason ?? null,
  };
}

export async function saveMarketingReportConfig(input: {
  enabled: boolean;
  hour: number;
  minute: number;
  recipientUserIds: string[];
}): Promise<MarketingReportConfigResponse> {
  try {
    const res = await api.post<{
      success: boolean;
      error?: string;
      config: MarketingReportConfig;
      recipients: MarketingReportRecipient[];
    }>("/api/marketing/report-config", input);
    if (!res.data.success) {
      throw new Error(res.data.error ?? "Não foi possível salvar o disparo do relatório.");
    }
    return {
      config: res.data.config,
      recipients: res.data.recipients ?? [],
      whatsappReady: true,
      whatsappReason: null,
    };
  } catch (err) {
    const status = (err as { response?: { status?: number; data?: { error?: string } } })?.response
      ?.status;
    const apiError = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
    if (apiError) throw new Error(apiError);
    if (status === 502 || status === 504) {
      throw new Error("O servidor não respondeu a tempo ao salvar. Tente de novo em alguns segundos.");
    }
    throw err instanceof Error ? err : new Error("Não foi possível salvar o disparo do relatório.");
  }
}

export interface MarketingReportTestResult {
  sent: number;
  failed: number;
  skipped: number;
  errors: string[];
  recipients: Array<{ userId: string; name: string; sent: boolean; reason?: string }>;
}

export async function sendMarketingReportTest(
  opts: { toAll?: boolean; userIds?: string[] } = {},
): Promise<MarketingReportTestResult> {
  const res = await api.post<{
    success: boolean;
    error?: string;
    sent?: number;
    failed?: number;
    skipped?: number;
    errors?: string[];
    recipients?: MarketingReportTestResult["recipients"];
  }>("/api/marketing/report/test", opts);
  if (!res.data.success && (res.data.sent ?? 0) <= 0) {
    throw new Error(res.data.error ?? "Não foi possível enviar o relatório de teste.");
  }
  return {
    sent: res.data.sent ?? 0,
    failed: res.data.failed ?? 0,
    skipped: res.data.skipped ?? 0,
    errors: res.data.errors ?? [],
    recipients: res.data.recipients ?? [],
  };
}

export interface MarketingGoalCounts {
  [kind: string]: number;
}

export interface MarketingGoalDay {
  date: string;
  counts: MarketingGoalCounts;
}

export interface MarketingGoalWeek {
  index: number;
  startDate: string;
  endDate: string;
  counts: MarketingGoalCounts;
  days: MarketingGoalDay[];
}

export interface MarketingGoalDistribution {
  mode: "auto" | "manual";
  basis: "calendar" | "weekdays";
  includeSaturday: boolean;
}

export interface MarketingGoalVersion {
  version: number;
  savedAt: string;
  savedByName: string;
  reason: string;
  month: string;
  counts: MarketingGoalCounts;
  distribution: MarketingGoalDistribution;
  weeks: MarketingGoalWeek[];
}

export async function fetchGoalMonth(month: string): Promise<{
  month: string;
  readOnly: boolean;
  version: MarketingGoalVersion | null;
  history: Array<{
    version: number;
    savedAt: string;
    savedByName: string;
    reason: string;
    counts: MarketingGoalCounts;
    changes: string[];
  }>;
  previousMonth: { month: string; counts: MarketingGoalCounts; distribution: MarketingGoalDistribution } | null;
}> {
  const res = await api.get<{
    success: boolean;
    error?: string;
    month: string;
    readOnly: boolean;
    version: MarketingGoalVersion | null;
    history: Array<{
      version: number;
      savedAt: string;
      savedByName: string;
      reason: string;
      counts: MarketingGoalCounts;
      changes: string[];
    }>;
    previousMonth: { month: string; counts: MarketingGoalCounts; distribution: MarketingGoalDistribution } | null;
  }>("/api/marketing/goals/month", { params: { month } });
  if (!res.data.success) throw new Error(res.data.error ?? "Não foi possível carregar as metas.");
  return res.data;
}

export async function saveGoalMonth(input: {
  month: string;
  counts: MarketingGoalCounts;
  distribution: MarketingGoalDistribution;
  weeks: MarketingGoalWeek[];
  reason?: string;
  confirmPastEdit?: boolean;
}): Promise<void> {
  const res = await api.post<{ success: boolean; error?: string; messages?: string[] }>(
    "/api/marketing/goals/month",
    input,
  );
  if (!res.data.success) throw new Error(res.data.error ?? "Não foi possível salvar as metas.");
}

export interface GoalTrackingPayload {
  month: string;
  today: string;
  hasGoal: boolean;
  readOnly: boolean;
  cards: Array<{
    kind: string;
    label: string;
    goal: number;
    realized: number;
    percent: number;
    remaining: number;
    projection: number;
    status: "no_ritmo" | "atrasado" | "batida" | "sem_meta";
    goalToDate: number;
    realizedToDate: number;
  }>;
  weeks: Array<{
    index: number;
    label: string;
    startDate: string;
    endDate: string;
    current: boolean;
    kinds: Array<{ kind: string; goal: number; realized: number; percent: number }>;
  }>;
  days: Array<{
    date: string;
    kinds: Array<{ kind: string; goal: number; realized: number }>;
  }>;
  publications: Array<{
    id: string;
    kind: string;
    date: string;
    permalink: string;
    caption: string;
    source: "instagram" | "manual";
    note?: string;
    createdByName?: string;
  }>;
  history: Array<{
    month: string;
    kinds: Array<{ kind: string; goal: number; realized: number; percent: number }>;
  }>;
}

export async function fetchGoalTracking(month: string): Promise<GoalTrackingPayload> {
  const res = await api.get<GoalTrackingPayload & { success: boolean; error?: string }>(
    "/api/marketing/goals/tracking",
    { params: { month } },
  );
  if (!res.data.success) throw new Error(res.data.error ?? "Não foi possível carregar o acompanhamento.");
  return res.data;
}

export async function saveManualGoalEntry(input: {
  kind: string;
  publishedOn: string;
  note?: string;
}): Promise<void> {
  const res = await api.post<{ success: boolean; error?: string }>("/api/marketing/goals/manual", input);
  if (!res.data.success) throw new Error(res.data.error ?? "Não foi possível lançar o ajuste.");
}
